import { useTar } from '@/modules'
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Switch,
} from '@duck/ui'
import { Bell, Mail } from 'lucide-react'
import { SettingRow } from '@/components/settings'
import { useState, useEffect } from 'react'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'

export const OrganizationSettingsPage = () => {
    const { t } = useTar()
    const { t: tP } = usePartnerTar()
    const { getOrgSetting, setOrgSetting, isLoading, settings } = useOrganizationSettings()

    // Initialize state from organization settings
    const [invitationEmails, setInvitationEmails] = useState(
        getOrgSetting('invitationEmails')
    )
    const [submissionEmails, setSubmissionEmails] = useState(
        getOrgSetting('submissionEmails')
    )

    // Update state when organization settings change
    useEffect(() => {
        if (settings) {
            if (settings.invitationEmails !== undefined) {
                setInvitationEmails(settings.invitationEmails)
            }
            if (settings.submissionEmails !== undefined) {
                setSubmissionEmails(settings.submissionEmails)
            }
        }
    }, [settings])

    const makeSettingsUpdateHandler = (setting: 'invitationEmails' | 'submissionEmails', cb: (checked: boolean) => void) =>
        async (checked: boolean) => {
            try {
                cb(checked)
                await setOrgSetting(setting, checked)
            } catch {
                // Revert on error
                cb(!checked)
            }
        }

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-4xl space-y-8 mt-8 mb-16 px-4">
                {/* Page Header */}
                <div className="space-y-2">
                    <h1 className="text-3xl font-bold text-foreground">
                        {t('organizationSettings.title')}
                    </h1>
                    <p className="text-muted-foreground">
                        {t('organizationSettings.description')}
                    </p>
                </div>

                {/* Settings Section */}
                <Card className="border-border">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-4">
                            {t('common.settings')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Invitation Emails */}
                        <SettingRow
                            Icon={Mail}
                            title={t('organizationSettings.invitationEmails')}
                            description={invitationEmails ? tP("invitationEmailsDescriptionON") : tP("invitationEmailsDescriptionOFF")}
                        >
                            <Switch
                                id="invitationEmails"
                                checked={invitationEmails}
                                onCheckedChange={makeSettingsUpdateHandler('invitationEmails', checked => setInvitationEmails(checked))}
                                disabled={isLoading}
                            />
                        </SettingRow>
                        {/* Submission Emails */}
                        <SettingRow
                            Icon={Bell}
                            title={t('organizationSettings.submissionNotifications')}
                            description={tP('submissionNotificationsDescription')}
                        >
                            <Switch
                                id="submissionEmails"
                                checked={submissionEmails}
                                onCheckedChange={makeSettingsUpdateHandler('submissionEmails', checked => setSubmissionEmails(checked))}
                                disabled={isLoading}
                            />
                        </SettingRow>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

